import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, collection, getDocs, deleteDoc, query, where, onSnapshot, addDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import './styles/customerWindow.css';
import { FaEnvelope, FaPhone, FaMoneyBillWave, FaGlobe, FaClock, FaPlus, FaLinkedin, FaTimes, FaTrash, FaLink, FaUser, FaUserCircle, FaUserTie } from 'react-icons/fa';
import Select from 'react-select';
import ProjectTaskTable from '../taskmanager/projects/projectTaskTable';
import DatePicker, { registerLocale } from 'react-datepicker';
import da from 'date-fns/locale/da';
import 'react-datepicker/dist/react-datepicker.css';
import GrundlæggendeInfo from './customerWindow/grundlæggendeInfo';
import Forretningsdetaljer from './customerWindow/forretningsdetaljer';
import LeadKilderOgNoter from './customerWindow/leadKilderOgNoter';
import Opdateringer from './customerWindow/opdateringer';

// Registrer dansk locale
registerLocale('da', da);

const STATUS_TYPES = {
  lead: { 
    label: 'Lead', 
    color: '#ec4899',
    icon: '🎯',
    description: 'Ny potentiel kunde'
  },
  contacted: { 
    label: 'Kontaktet', 
    color: '#eab308',
    icon: '📞',
    description: 'Første kontakt etableret'
  },
  qualified: { 
    label: 'Kvalificeret', 
    color: '#f97316',
    icon: '✓',
    description: 'Bekræftet potentiale'
  },
  meeting: { 
    label: 'Møde', 
    color: '#2563eb',
    icon: '📅',
    description: 'Møde planlagt/afholdt'
  },
  ongoing: { 
    label: 'Igangværende', 
    color: '#9333ea',
    icon: '⚡',
    description: 'Aktivt samarbejde'
  },
  won: { 
    label: 'Vundet', 
    color: '#22c55e',
    icon: '🏆',
    description: 'Aftale indgået'
  },
  notInterested: { 
    label: 'Ikke interesseret', 
    color: '#6b7280',
    icon: '✕',
    description: 'Ingen interesse'
  }
};

const STATUS_ORDER = [
  'lead',
  'contacted',
  'qualified',
  'meeting',
  'ongoing',
  'won',
  'notInterested'
];

const PRODUCTS = [
  { value: 'website', label: 'Hjemmeside', icon: '🌐' },
  { value: 'video', label: 'Video', icon: '🎥' },
  { value: 'marketing', label: 'Marketing', icon: '📢' },
  { value: 'automation', label: 'Automatisering', icon: '⚡' }
];

const PAYMENT_TYPES = [
  { id: 'oneTime', label: 'Enkeltbeløb', icon: '💰' },
  { id: 'subscription', label: 'Abonnement', icon: '🔄' }
];

const LEAD_SOURCES = [
  { value: 'linkedin', label: 'LinkedIn', icon: '💼' },
  { value: 'leadmagnet', label: 'Leadmagnet', icon: '🧲' },
  { value: 'coldCall', label: 'Cold Call', icon: '📞' },
  { value: 'ads', label: 'Ads', icon: '📣' },
  { value: 'referral', label: 'Referral', icon: '👥' },
  { value: 'website', label: 'Website', icon: '🌐' },
  { value: 'other', label: 'Other', icon: '📌' }
];

// Custom Option Component
const CustomOption = ({ children, ...props }) => {
  const { data } = props;
  return (
    <div
      {...props.innerProps}
      className={`custom-option ${props.isFocused ? 'focused' : ''}`}
    >
      <span className="option-icon">{data.icon}</span>
      {children}
    </div>
  );
};

// Custom Multi-Value Component
const CustomMultiValue = ({ children, ...props }) => {
  const { data } = props;
  return (
    <div className="custom-multi-value">
      <span className="value-icon">{data.icon}</span>
      {children}
    </div>
  );
};

// Tilpas styling konfiguration for react-select
const selectStyles = {
  control: (base, state) => ({
    ...base,
    background: 'rgba(255, 255, 255, 0.03)',
    borderColor: state.isFocused ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
    boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#2563eb' : 'rgba(255, 255, 255, 0.2)'
    }
  }),
  menu: (base) => ({
    ...base,
    background: '#010510',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    background: isSelected 
      ? 'rgba(37, 99, 235, 0.2)' 
      : isFocused 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'transparent',
    color: '#ffffff',
    cursor: 'pointer',
    '&:active': {
      background: 'rgba(37, 99, 235, 0.3)'
    }
  }),
  multiValue: (base) => ({
    ...base,
    background: 'rgba(37, 99, 235, 0.1)',
    border: '1px solid rgba(37, 99, 235, 0.2)',
    borderRadius: '16px',
    padding: '2px 8px',
    gap: '6px',
    '&:hover': {
      background: 'rgba(37, 99, 235, 0.15)',
      border: '1px solid rgba(37, 99, 235, 0.3)',
    }
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#ffffff',
    padding: '2px',
    fontSize: '0.9rem'
  }),
  multiValueRemove: (base) => ({
    ...base,
    display: 'none'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '6px 8px',
    gap: '4px'
  }),
  clearIndicator: (base) => ({
    ...base,
    display: 'none'
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.4)',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.8)'
    }
  }),
  input: (base) => ({
    ...base,
    color: '#ffffff'
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.3)'
  }),
  singleValue: (base) => ({
    ...base,
    color: '#ffffff'
  })
};

const formatCurrency = (value) => {
  if (!value) return '0,00';
  
  // Konverter til nummer og afrund til 2 decimaler
  const number = Number(value);
  
  // Formater med tusindtalsseparator og decimaler
  return number.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }) + ',-';
};

function CustomerWindow({ customer, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    ...customer,
    status: customer.status || 'lead',
    products: customer.products || [],
    paymentType: customer.paymentType || '',
    leadSources: customer.leadSources || [],
    estimatedValue: customer.estimatedValue || '',
    websiteUrl: customer.websiteUrl || '',
    linkedinUrl: customer.linkedinUrl || '',
    updates: customer.updates || []
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const windowRef = useRef(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [linkedProject, setLinkedProject] = useState(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  useEffect(() => {
    setFormData({
      ...customer,
      status: customer.status || 'lead',
      products: customer.products || [],
      paymentType: customer.paymentType || '',
      leadSources: customer.leadSources || [],
      estimatedValue: customer.estimatedValue || '',
      websiteUrl: customer.websiteUrl || '',
      linkedinUrl: customer.linkedinUrl || '',
      updates: customer.updates || []
    });
    fetchUsers();
  }, [customer]);

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Fejl ved hentning af brugere:', error);
    }
  };

  const debouncedSave = useCallback(
    debounce(async (data) => {
      try {
        setSaving(true);
        await updateDoc(doc(db, 'customers', customer.id), data);
        onUpdate();
        setHasChanges(false);
      } catch (error) {
        console.error('Fejl ved auto-save:', error);
      } finally {
        setSaving(false);
      }
    }, 1000),
    [customer.id, onUpdate]
  );

  useEffect(() => {
    if (hasChanges) {
      debouncedSave(formData);
    }
  }, [formData, hasChanges, debouncedSave]);

  const debouncedUpdate = useCallback(
    debounce(async (updates) => {
      if (!customer?.id) return;
      
      try {
        const docRef = doc(db, 'customers', customer.id);
        await updateDoc(docRef, {
          ...updates,
          lastModified: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error updating document:', error);
      }
    }, 1000),
    [customer?.id]
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
      [name]: value
    }));

    // Debounce Firestore update
    debouncedUpdate({
      [name]: value
    });
  }, [debouncedUpdate]);

  const handleArrayChange = useCallback((name, values) => {
      setFormData(prev => ({
        ...prev,
      [name]: values
    }));

    // Opdater Firestore med det samme for array ændringer
    const docRef = doc(db, 'customers', customer.id);
    updateDoc(docRef, {
      [name]: values,
      lastModified: new Date().toISOString()
    }).catch(error => {
      console.error('Error updating document:', error);
    });
  }, [customer?.id]);

  const handleStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      setFormData(prev => ({
        ...prev,
        status: newStatus
      }));
      
      await updateDoc(doc(db, 'customers', customer.id), {
        status: newStatus
      });
      
      onUpdate();
    } catch (error) {
      console.error('Fejl ved statusændring:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleMultiSelect = (e, array) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [array]: prev[array].includes(value)
        ? prev[array].filter(item => item !== value)
        : [...prev[array], value]
    }));
  };

  const handleAddUpdate = async () => {
    const newUpdate = {
      id: Date.now().toString(),
      text: '',
      timestamp: new Date().toISOString()
    };

    const newUpdates = [newUpdate, ...formData.updates];
    
    setFormData(prev => ({
      ...prev,
      updates: newUpdates
    }));

    // Gem med det samme i databasen
    try {
      await updateDoc(doc(db, 'customers', customer.id), {
        updates: newUpdates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved tilføjelse af opdatering:', error);
    }
  };

  const handleUpdateChange = async (id, text) => {
    const newUpdates = formData.updates.map(update => 
      update.id === id ? { ...update, text } : update
    );

    setFormData(prev => ({
      ...prev,
      updates: newUpdates
    }));

    // Gem ændringen i databasen
    try {
      await updateDoc(doc(db, 'customers', customer.id), {
        updates: newUpdates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved opdatering af tekst:', error);
    }
  };

  const handleUpdateDateChange = async (id, date) => {
    const newUpdates = formData.updates.map(update => 
      update.id === id ? { ...update, timestamp: date.toISOString() } : update
    );

    setFormData(prev => ({
      ...prev,
      updates: newUpdates
    }));

    // Gem ændringen i databasen
    try {
      await updateDoc(doc(db, 'customers', customer.id), {
        updates: newUpdates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved opdatering af dato:', error);
    }
  };

  const handleDeleteUpdate = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne opdatering?')) return;

    const newUpdates = formData.updates.filter(update => update.id !== id);

    setFormData(prev => ({
      ...prev,
      updates: newUpdates
    }));

    // Gem ændringen i databasen
    try {
      await updateDoc(doc(db, 'customers', customer.id), {
        updates: newUpdates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved sletning af opdatering:', error);
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await deleteDoc(doc(db, 'customers', customer.id));
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Fejl ved sletning af kunde:', error);
    }
  };

  const handleClickOutside = useCallback((event) => {
    if (windowRef.current && 
        !windowRef.current.contains(event.target) && 
        !event.target.closest('.customer-card')) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('customer-window-overlay')) {
      onClose();
    }
  };

  // Hent kundens tilknyttede projekt
  useEffect(() => {
    if (!customer.projectId) return;

    const unsubscribe = onSnapshot(doc(db, 'projects', customer.projectId), (doc) => {
      if (doc.exists()) {
        setLinkedProject({ id: doc.id, ...doc.data() });
      }
    });

    return () => unsubscribe();
  }, [customer.projectId]);

  // Hent tilgængelige projekter (uden customerId)
  useEffect(() => {
    if (!showProjectSelector) return;

    console.log('Henter projekter...');

    const q = query(
      collection(db, 'projects'),
      where('customerId', '==', null)  // Kun søg efter null
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      console.log('Snapshot modtaget:', snapshot.docs.length, 'dokumenter');
      
      // Hent projekter
      const projectsData = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();
          console.log('Projekt data:', { id: doc.id, ...data });
          
          // Hent antal opgaver for hvert projekt
          const tasksQuery = query(
            collection(db, 'tasks'),
            where('projectId', '==', doc.id)
          );
          const taskSnapshot = await getDocs(tasksQuery);
          
          return {
            id: doc.id,
            ...data,
            taskCount: taskSnapshot.size
          };
        })
      );
      
      console.log('Behandlet projektdata:', projectsData);
      
      // Filtrer projekter baseret på søgning
      const filtered = projectSearch
        ? projectsData.filter(project => 
            project.name.toLowerCase().includes(projectSearch.toLowerCase())
          )
        : projectsData;
      
      console.log('Filtrerede projekter:', filtered);
      
      setAvailableProjects(projectsData);
      setFilteredProjects(filtered);
    });

    return () => unsubscribe();
  }, [showProjectSelector, projectSearch]);

  // Funktion til at oprette nyt projekt
  const handleCreateProject = async () => {
    try {
      const newProject = {
        name: `Projekt for ${formData.name}`,
        description: '',
        status: 'todo',
        customerId: customer.id,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser.uid,
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'projects'), newProject);
      
      // Opdater kunden med reference til det nye projekt
      await updateDoc(doc(db, 'customers', customer.id), {
        projectId: docRef.id,
        updatedAt: new Date().toISOString()
      });

      // Sæt det nye projekt som linkedProject med det samme
      setLinkedProject({
        id: docRef.id,
        ...newProject
      });
      
    } catch (error) {
      console.error('Fejl ved oprettelse af projekt:', error);
    }
  };

  // Funktion til at tilknytte eksisterende projekt
  const handleLinkProject = async (projectId) => {
    try {
      // Opdater projektet med customerId
      await updateDoc(doc(db, 'projects', projectId), {
        customerId: customer.id,
        updatedAt: new Date().toISOString()
      });

      // Opdater kunden med projectId
      await updateDoc(doc(db, 'customers', customer.id), {
        projectId: projectId,
        updatedAt: new Date().toISOString()
      });

      setShowProjectSelector(false);
    } catch (error) {
      console.error('Fejl ved tilknytning af projekt:', error);
    }
  };

  return (
    <>
      <div className="customer-window-overlay" onClick={handleOverlayClick}></div>
      <div className="customer-window-wrapper">
        <div className="customer-window" ref={windowRef}>
          <div className="window-header">
            <div className="header-content">
              <div className="customer-name-edit">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="name-input"
                  placeholder="Indtast kundens navn"
                />
                <div className="customer-id">ID: {customer.id}</div>
                <div className={`save-status ${saving ? 'saving' : hasChanges ? 'unsaved' : 'saved'}`}>
                  {saving ? 'Gemmer...' : hasChanges ? 'Ændringer ikke gemt' : 'Alle ændringer er gemt'}
                </div>
              </div>
            </div>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>

          <form onSubmit={e => e.preventDefault()}>
            {/* Status Funnel Section */}
            <div className="form-section status-section">
              <div className="section-header">
                <h3>Status</h3>
                <div className="section-description">
                  Kundens nuværende status i salgspipelinen
                </div>
              </div>
              <div className="status-progress">
                <div className="status-track">
                  {STATUS_ORDER.map((statusKey, index) => {
                    const status = STATUS_TYPES[statusKey];
                    const isActive = formData.status === statusKey;
                    const isPassed = STATUS_ORDER.indexOf(formData.status) > index;
                    const isLast = statusKey === 'won' || statusKey === 'notInterested';
                    const showLine = !isLast && statusKey !== 'notInterested';
                    
                    return (
                      <div 
                        key={statusKey}
                        className={`status-step ${isActive ? 'active' : ''} ${isPassed ? 'passed' : ''} ${isLast ? 'last' : ''}`}
                      >
                        <button
                          className="status-button"
                          onClick={() => handleStatusChange(statusKey)}
                          style={{ 
                            '--status-color': status.color
                          }}
                          title={status.description}
                        >
                          <span className="status-icon">{status.icon}</span>
                          <span className="status-label">{status.label}</span>
                        </button>
                        {showLine && <div className="status-line" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Estimeret værdi Section */}
            <div className="form-section estimated-value-section">
              <div className="section-header">
                <h3>Estimeret værdi</h3>
                <div className="section-description">
                  Potentiel værdi af denne kunde
                </div>
              </div>
              <div className="estimated-value-display">
                <div className="input-with-icon value-input-wrapper">
                  <FaMoneyBillWave className="input-icon" />
                  <input
                    type="text"
                    name="estimatedValue"
                    value={formatCurrency(formData.estimatedValue)}
                    onChange={(e) => {
                      const numbers = e.target.value.replace(/[^0-9]/g, '');
                      const value = numbers ? Number(numbers) / 100 : '';
                      handleChange({
                        target: {
                          name: 'estimatedValue',
                          value: value
                        }
                      });
                    }}
                    placeholder="0,00"
                  />
                  <span className="currency">DKK inkl. moms</span>
                </div>
              </div>
            </div>

            {/* Main Info Section */}
            <GrundlæggendeInfo 
              formData={formData}
              handleChange={handleChange}
              users={users}
            />

            {/* Business Details Section */}
            <Forretningsdetaljer 
              formData={formData}
              handleChange={handleChange}
              handleArrayChange={handleArrayChange}
            />

            {/* Lead Sources & Notes Section */}
            <LeadKilderOgNoter 
              formData={formData}
              handleChange={handleChange}
              setFormData={setFormData}
              handleArrayChange={handleArrayChange}
            />

            {/* Updates Section */}
            <Opdateringer 
              formData={formData}
              handleAddUpdate={handleAddUpdate}
              handleUpdateDateChange={handleUpdateDateChange}
              handleDeleteUpdate={handleDeleteUpdate}
              handleUpdateChange={handleUpdateChange}
            />

            {/* Next Contact Section */}
            <div className="form-section next-contact-section">
              <div className="section-header">
                <h3>Næste kontakt</h3>
                <div className="section-description">
                  Hvornår skal der følges op på dette lead?
                </div>
              </div>
              <div className="next-contact-picker">
                <DatePicker
                  selected={formData.nextContact ? new Date(formData.nextContact) : null}
                  onChange={(date) => {
                    const nextContact = date ? date.toISOString() : null;
                    setFormData(prev => ({
                      ...prev,
                      nextContact
                    }));
                    debouncedUpdate({
                      nextContact
                    });
                  }}
                  locale="da"
                  dateFormat="d. MMMM yyyy HH:mm"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  placeholderText="Vælg tidspunkt for næste kontakt"
                  isClearable
                  className="date-picker-input"
                  popperClassName="date-picker-popper"
                />
              </div>
            </div>
          </form>

          {/* Opdater projekt sektionen */}
          <div className="window-section">
            <div className="section-header">
              <h2>Projekt</h2>
              {!linkedProject && (
                <div className="project-actions">
                  <button className="action-button" onClick={handleCreateProject}>
                    <FaPlus />
                    <span>Opret projekt</span>
                  </button>
                </div>
              )}
            </div>

            {linkedProject && (
              <ProjectTaskTable project={linkedProject} />
            )}
          </div>

          {/* Danger Zone Section - Vis kun hvis kunden har eksisteret i mere end 1 minut */}
          {customer.createdAt && 
           new Date().getTime() - new Date(customer.createdAt).getTime() > 60000 && (
            <div className="form-section danger-zone">
              <div className="section-header">
                <h3>Farlig zone</h3>
                <div className="section-description">
                  Permanente handlinger der ikke kan fortrydes
                </div>
              </div>
              <button 
                type="button" 
                className="delete-customer-button"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Slet kunde
              </button>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="delete-confirm-overlay">
              <div className="delete-confirm-dialog">
                <h3>Slet kunde</h3>
                <p>Er du sikker på, at du vil slette denne kunde? Dette kan ikke fortrydes.</p>
                <div className="delete-confirm-actions">
                  <button 
                    className="cancel-button"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Annuller
                  </button>
                  <button 
                    className="delete-confirm-button"
                    onClick={handleDeleteCustomer}
                  >
                    Ja, slet kunde
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default CustomerWindow;
